import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Icon, type IconName } from './Icon';
import { titleCase } from '../lib/titlecase';

export type EmptyAction = { label: string; onClick?: () => void; to?: string; primary?: boolean; disabled?: boolean };

/**
 * What a table, list or chart shows with no rows. Spec §9: every empty state says how to fill
 * it, so `children` should name the step ("Add keywords…", "Connect Search Console…") and
 * `action` should be that step when the user can take it. Has `data-testid="empty-state"`.
 */
export function EmptyState({
  title,
  children,
  action,
  secondary,
  icon = 'inbox',
  compact,
}: {
  title: string;
  /** How to fill it. */
  children?: ReactNode;
  action?: EmptyAction;
  secondary?: EmptyAction;
  icon?: IconName;
  compact?: boolean;
}) {
  return (
    <div className={`empty-state${compact ? ' compact' : ''}`} data-testid="empty-state">
      {compact ? null : (
        <span className="es-ic">
          <Icon name={icon} />
        </span>
      )}
      <h3>{titleCase(title)}</h3>
      {children ? <p className="es-b">{children}</p> : null}
      {action || secondary ? (
        <div className="es-a">
          {action ? <ActionButton a={action} primary={action.primary ?? true} /> : null}
          {secondary ? <ActionButton a={secondary} primary={false} /> : null}
        </div>
      ) : null}
    </div>
  );
}

function ActionButton({ a, primary }: { a: EmptyAction; primary: boolean }) {
  const cls = `btn${primary ? ' primary' : ''}`;
  if (a.to && !a.disabled) {
    return (
      <Link className={cls} to={a.to}>
        {a.label}
      </Link>
    );
  }
  return (
    <button type="button" className={cls} onClick={a.onClick} disabled={a.disabled}>
      {a.label}
    </button>
  );
}
